import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import './VideoUpload.css';

const VideoUpload = ({ onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check if file is a video
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please select a valid video file.');
        setFile(null);
        return;
      }
      
      // Check file size (limit to 500MB)
      if (selectedFile.size > 500 * 1024 * 1024) {
        setError('File size exceeds 500MB limit.');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError(null);
      
      // Auto-populate title from filename (without extension)
      const fileName = selectedFile.name;
      const titleFromFileName = fileName.substring(0, fileName.lastIndexOf('.'));
      setTitle(titleFromFileName);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a video file.');
      return;
    }
    
    if (!title.trim()) {
      setError('Please enter a title for the video.');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      
      console.log('Initiating video upload...');
      console.log('File:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Get the current auth session to include the token in the request
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      
      if (!idToken) {
        throw new Error('No authentication token available');
      }
      
      // Step 1: Get pre-signed URL for upload
      console.log('Requesting pre-signed URL...');
      const apiUrl = 'https://f8r84a0hqe.execute-api.us-east-1.amazonaws.com/prod/videos/upload';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `${idToken.toString()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          title: title,
          description: description
        })
      });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      console.log('Pre-signed URL response received');
      
      const data = await response.json();
      console.log('Parsed response data:', data);
      
      const { uploadUrl, videoId } = data;
      console.log('Upload URL:', uploadUrl);
      console.log('Video ID:', videoId);
      
      // Step 2: Upload the file to S3 using the pre-signed URL
      console.log('Starting S3 upload...');
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      
      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          console.log(`Upload progress: ${percentComplete}%`);
          setUploadProgress(percentComplete);
        }
      };
      
      // Handle upload completion
      xhr.onload = async () => {
        console.log('S3 upload completed with status:', xhr.status);
        if (xhr.status === 200) {
          // Step 3: Update video metadata to mark upload as complete
          console.log('Updating video status to uploaded...');
          
          const updateResponse = await fetch(`https://f8r84a0hqe.execute-api.us-east-1.amazonaws.com/prod/videos/${videoId}`, {
            method: 'PUT',
            headers: {
              'Authorization': `${idToken.toString()}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              status: 'uploaded'
            })
          });
          
          if (!updateResponse.ok) {
            throw new Error(`API returned status ${updateResponse.status}`);
          }
          
          console.log('Video upload process completed successfully');
          
          // Reset form
          setFile(null);
          setTitle('');
          setDescription('');
          setUploadProgress(0);
          setUploading(false);
          
          // Notify parent component of successful upload
          onUploadSuccess();
        } else {
          console.error('S3 upload failed with status:', xhr.status);
          throw new Error('Upload failed');
        }
      };
      
      // Handle upload errors
      xhr.onerror = (e) => {
        console.error('XHR error during upload:', e);
        setError('Upload failed. Please try again.');
        setUploading(false);
      };
      
      // Start the upload
      console.log('Sending file to S3...');
      xhr.send(file);
      
    } catch (err) {
      console.error('Error uploading video:', err);
      if (err.response) {
        console.error('Error response:', err.response);
      }
      if (err.message) {
        console.error('Error message:', err.message);
      }
      setError('Failed to upload video. Please try again.');
      setUploading(false);
    }
  };

  return (
    <div className="video-upload-container">
      <h2>Upload New Video</h2>
      
      {error && <p className="error-message">{error}</p>}
      
      <form onSubmit={handleSubmit} className="upload-form">
        <div className="form-group">
          <label htmlFor="video-file">Select Video:</label>
          <input
            type="file"
            id="video-file"
            accept="video/*"
            onChange={handleFileChange}
            disabled={uploading}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="video-title">Title:</label>
          <input
            type="text"
            id="video-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={uploading}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="video-description">Description:</label>
          <textarea
            id="video-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={uploading}
            rows="3"
          />
        </div>
        
        {uploading ? (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p>{uploadProgress}% Uploaded</p>
            <p className="upload-note">
              Please wait while your video is being uploaded. This may take several minutes depending on your file size and internet connection.
            </p>
          </div>
        ) : (
          <button 
            type="submit" 
            className="upload-button"
            disabled={!file || !title.trim()}
          >
            Upload Video
          </button>
        )}
      </form>
    </div>
  );
};

export default VideoUpload;
